/**
 * Supabase Realtime Hooks
 *
 * Real-time subscriptions for messages, notifications, and presence.
 * These hooks integrate with React Query for cache synchronization.
 */

import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from './client';
import { mode } from '@/lib/config/app-mode';
import { queryKeys } from './api';
import { reportError } from '@/lib/errors';
import { isRelationshipDenied } from './messaging-mappers';
import { updateBadgeCount } from '@/lib/notifications/push';
import type { Message, Notification } from './types';

// Presence payload types for typed presence state access
interface TypingPresencePayload {
  id: string;
  name: string;
  isTyping: boolean;
  online_at: string;
}

interface OnlinePresencePayload {
  id: string;
  name: string;
  online_at: string;
}

// ============================================
// Types
// ============================================

export interface TypingUser {
  id: string;
  name: string;
  isTyping: boolean;
  conversationId: string;
}

export interface PresenceState {
  id: string;
  name: string;
  online: boolean;
  lastSeen: string;
}

export interface RealtimeNotification {
  id: string;
  type: 'message' | 'data_room_request' | 'data_room_approved' | 'profile_view' | 'score_update' | 'feedback_update' | 'waitlist_invited' | 'system';
  title: string;
  body: string;
  data?: Record<string, any>;
  createdAt: string;
  read: boolean;
}

// ============================================
// Real-time Messages Hook
// ============================================

/**
 * Subscribe to real-time message updates for a conversation
 */
export function useRealtimeMessages(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!conversationId || !isSupabaseConfigured() || mode.isMock) {
      return;
    }

    // Create channel for this conversation
    const channel = supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload: RealtimePostgresChangesPayload<Message>) => {
          // Update React Query cache with new message
          queryClient.setQueryData(
            queryKeys.messages(conversationId),
            (oldMessages: Message[] | undefined) => {
              if (!oldMessages) return [payload.new as Message];
              // Avoid duplicates
              const exists = oldMessages.some((m) => m.id === (payload.new as Message).id);
              if (exists) return oldMessages;
              return [...oldMessages, payload.new as Message];
            }
          );

          // Also invalidate conversations to update last message
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
        }
      )
      // NO UPDATE subscription on `messages`. It existed only to carry read
      // receipts, which are now derived from the per-side marks on
      // `conversations` — and that table already has its own subscription below,
      // so receipts arrive live without watching messages for a change nothing
      // makes. There is no client path that updates a message at all.
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [conversationId, queryClient]);

  return channelRef.current;
}

// ============================================
// Real-time Conversations Hook
// ============================================

/**
 * Subscribe to real-time conversation updates
 */
export function useRealtimeConversations(
  userId: string | undefined,
  userType: 'startup' | 'investor'
) {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!userId || !isSupabaseConfigured() || mode.isMock) {
      return;
    }

    const filterColumn = userType === 'startup' ? 'startup_id' : 'investor_profile_id';

    const channel = supabase
      .channel(`conversations:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all changes
          schema: 'public',
          table: 'conversations',
          filter: `${filterColumn}=eq.${userId}`,
        },
        () => {
          // Invalidate conversations query to refetch
          queryClient.invalidateQueries({ queryKey: queryKeys.conversations(userId) });
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [userId, userType, queryClient]);

  return channelRef.current;
}

// ============================================
// Typing Indicators Hook
// ============================================

/**
 * Track typing status in a conversation using Supabase Presence
 */
export function useTypingIndicator(
  conversationId: string | undefined,
  currentUser: { id: string; name: string } | null
) {
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!conversationId || !currentUser || !isSupabaseConfigured() || mode.isMock) {
      return;
    }

    const channel = supabase.channel(`typing:${conversationId}`, {
      config: {
        presence: {
          key: currentUser.id,
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<TypingPresencePayload>();
        const typing: TypingUser[] = [];

        Object.keys(state).forEach((key) => {
          const presences = state[key];
          presences.forEach((presence) => {
            if (presence.isTyping && presence.id !== currentUser.id) {
              typing.push({
                id: presence.id,
                name: presence.name,
                isTyping: true,
                conversationId,
              });
            }
          });
        });

        setTypingUsers(typing);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            id: currentUser.id,
            name: currentUser.name,
            isTyping: false,
            online_at: new Date().toISOString(),
          });
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [conversationId, currentUser]);

  const setTyping = useCallback(
    async (isTyping: boolean) => {
      if (!channelRef.current || !currentUser) return;

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      await channelRef.current.track({
        id: currentUser.id,
        name: currentUser.name,
        isTyping,
        online_at: new Date().toISOString(),
      });

      // Auto-clear typing after 3 seconds
      if (isTyping) {
        typingTimeoutRef.current = setTimeout(async () => {
          if (channelRef.current) {
            await channelRef.current.track({
              id: currentUser.id,
              name: currentUser.name,
              isTyping: false,
              online_at: new Date().toISOString(),
            });
          }
        }, 3000);
      }
    },
    [currentUser]
  );

  return { typingUsers, setTyping };
}

// ============================================
// Presence Hook (Online/Offline Status)
// ============================================

/**
 * Track user presence (online/offline status)
 */
export function usePresence(
  currentUser: { id: string; name: string } | null,
  roomId: string = 'global'
) {
  const [onlineUsers, setOnlineUsers] = useState<Map<string, PresenceState>>(new Map());
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!currentUser || !isSupabaseConfigured() || mode.isMock) {
      return;
    }

    const channel = supabase.channel(`presence:${roomId}`, {
      config: {
        presence: {
          key: currentUser.id,
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<OnlinePresencePayload>();
        const users = new Map<string, PresenceState>();

        Object.keys(state).forEach((key) => {
          const presences = state[key];
          if (presences.length > 0) {
            const presence = presences[0];
            users.set(key, {
              id: presence.id,
              name: presence.name,
              online: true,
              lastSeen: presence.online_at,
            });
          }
        });

        setOnlineUsers(users);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        if (newPresences.length > 0) {
          const presence = newPresences[0] as unknown as OnlinePresencePayload;
          setOnlineUsers((prev) => {
            const next = new Map(prev);
            next.set(key, {
              id: presence.id,
              name: presence.name,
              online: true,
              lastSeen: presence.online_at,
            });
            return next;
          });
        }
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        setOnlineUsers((prev) => {
          const next = new Map(prev);
          const user = next.get(key);
          if (user) {
            next.set(key, { ...user, online: false, lastSeen: new Date().toISOString() });
          }
          return next;
        });
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            id: currentUser.id,
            name: currentUser.name,
            online_at: new Date().toISOString(),
          });
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [currentUser, roomId]);

  const isUserOnline = useCallback(
    (userId: string) => {
      return onlineUsers.get(userId)?.online ?? false;
    },
    [onlineUsers]
  );

  return { onlineUsers, isUserOnline };
}

// ============================================
// Notifications Hook
// ============================================

/**
 * Subscribe to real-time notifications.
 *
 * TAKES THE AUTH USER ID. `notifications.user_id` references `profiles(id)`,
 * which IS the auth user id — not `investor_profiles.id` or
 * `startup_profiles.id`. `/notifications` used to pass the profile id, so the
 * query matched nothing and the screen was empty for every real user, while
 * the dashboard bell (which passed the auth id correctly) showed a red dot
 * from the same table. That is the whole "badge with no notifications" bug:
 * two callers, two id spaces, one of them wrong.
 *
 * BACKED BY REACT QUERY, not useState. Each caller used to hold its OWN copy
 * of the list and the unread count, and the realtime subscription only
 * listened for INSERT — so marking something read on the notifications screen
 * never reached the bell rendered by the dashboard, and the dot survived until
 * a remount. One shared cache key means every consumer sees one truth.
 */
export function useRealtimeNotifications(userId: string | undefined) {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const enabled = !!userId && isSupabaseConfigured() && !mode.isMock;
  // Memoised: a fresh array each render would resubscribe the realtime channel
  // on every render and defeat the shared cache key.
  const key = useMemo(() => queryKeys.notifications(userId ?? ''), [userId]);

  const { data: notifications = [] } = useQuery({
    queryKey: key,
    enabled,
    queryFn: async (): Promise<RealtimeNotification[]> => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId!)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;

      return (data ?? []).map((n) => ({
        id: n.id,
        type: n.type as RealtimeNotification['type'],
        title: n.title,
        body: n.body,
        data: (n.data ?? undefined) as Record<string, unknown> | undefined,
        createdAt: n.created_at ?? '',
        read: n.read ?? false,
      }));
    },
  });

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Live inserts. Read-state changes no longer need a subscription: they go
  // through the shared cache below.
  useEffect(() => {
    if (!enabled) return;

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload: RealtimePostgresChangesPayload<Notification>) => {
          const n = payload.new as Notification;
          queryClient.setQueryData<RealtimeNotification[]>(key, (prev = []) => {
            // The realtime event can race the refetch that follows a write;
            // without this an arriving notification could appear twice.
            if (prev.some((existing) => existing.id === n.id)) return prev;
            return [
              {
                id: n.id,
                type: n.type as RealtimeNotification['type'],
                title: n.title,
                body: n.body,
                data: (n.data ?? undefined) as Record<string, unknown> | undefined,
                createdAt: n.created_at ?? '',
                read: n.read ?? false,
              },
              ...prev,
            ];
          });
        }
      )
      .subscribe();

    channelRef.current = channel;
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [enabled, userId, queryClient, key]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!enabled) return;

      // Optimistic, then reconciled by the error check below. supabase-js
      // resolves with `{ error }` rather than throwing, so an unchecked write
      // here would leave the UI reading "read" over a row that never changed
      // (ARCH-06).
      queryClient.setQueryData<RealtimeNotification[]>(key, (prev = []) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );

      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) {
        reportError(error, { context: 'useRealtimeNotifications.markAsRead' });
        queryClient.invalidateQueries({ queryKey: key });
        return;
      }
      // Keep the OS badge in step with what the user has actually read.
      // Previously it was only recomputed on a push event, so the app-icon dot
      // outlived the notifications that caused it.
      void updateBadgeCount(userId!);
    },
    [enabled, userId, queryClient, key]
  );

  const markAllAsRead = useCallback(async () => {
    if (!enabled) return;

    queryClient.setQueryData<RealtimeNotification[]>(key, (prev = []) =>
      prev.map((n) => ({ ...n, read: true }))
    );

    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId!)
      .eq('read', false);

    if (error) {
      reportError(error, { context: 'useRealtimeNotifications.markAllAsRead' });
      queryClient.invalidateQueries({ queryKey: key });
      return;
    }
    void updateBadgeCount(userId!);
  }, [enabled, userId, queryClient, key]);

  /**
   * Delete one notification.
   *
   * There was no way to remove a notification at all — only to mark it read —
   * so the list only ever grew. `notifications.tsx` even imported a `Trash2`
   * icon and never rendered it, which is the shape of an intention that was
   * never finished.
   *
   * Bound by `user_id` as well as `id`: RLS already restricts writes to
   * `user_id = auth.uid()`, but stating it here means a wrong id fails visibly
   * in development instead of silently matching nothing (ARCH-06's lesson —
   * supabase-js reports failure via `{ error }`, so an unbound delete that
   * matches zero rows looks exactly like success).
   *
   * Optimistic, with the same rollback shape as `markAsRead`: the row leaves
   * the list immediately and the query is invalidated if the write is refused.
   */
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      if (!enabled) return;

      const previous = queryClient.getQueryData<RealtimeNotification[]>(key);
      queryClient.setQueryData<RealtimeNotification[]>(key, (prev = []) =>
        prev.filter((n) => n.id !== notificationId)
      );

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', userId!);

      if (error) {
        reportError(error, { context: 'useRealtimeNotifications.deleteNotification' });
        // Put it back rather than leave the list lying about what exists.
        if (previous) queryClient.setQueryData(key, previous);
        queryClient.invalidateQueries({ queryKey: key });
        return;
      }
      // A deleted notification may have been unread, so the OS badge moves too.
      void updateBadgeCount(userId!);
    },
    [enabled, userId, queryClient, key]
  );

  /** Clear the whole list. Same contract as `deleteNotification`, in bulk. */
  const clearAllNotifications = useCallback(async () => {
    if (!enabled) return;

    const previous = queryClient.getQueryData<RealtimeNotification[]>(key);
    queryClient.setQueryData<RealtimeNotification[]>(key, []);

    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', userId!);

    if (error) {
      reportError(error, { context: 'useRealtimeNotifications.clearAllNotifications' });
      if (previous) queryClient.setQueryData(key, previous);
      queryClient.invalidateQueries({ queryKey: key });
      return;
    }
    void updateBadgeCount(userId!);
  }, [enabled, userId, queryClient, key]);

  /**
   * Refetch the list. Exposed so pull-to-refresh can actually refresh.
   *
   * The screen used to `await new Promise(r => setTimeout(r, 1000))` and call
   * that a refresh — its own comment said "in a real app, this would refetch
   * notifications". It was allowlisted under ARCH-16 as mock-mode simulated
   * latency, but it sits behind no mock branch: every user got a one-second
   * spinner and the same stale list.
   */
  const refresh = useCallback(async () => {
    if (!enabled) return;
    await queryClient.invalidateQueries({ queryKey: key });
  }, [enabled, queryClient, key]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refresh,
  };
}

// ============================================
// Mark Messages as Read
// ============================================

/**
 * What marking a thread read did.
 *   'marked'    — the caller's read row was written.
 *   'read_only' — the thread is read-only (the company closed, owner decision
 *                 #9: RESTRICTIVE write policies refuse every write to its
 *                 conversation_reads). Expected, not an error: nothing to show,
 *                 nothing to report, and the caller must not retry.
 *   'failed'    — anything else; reported (the badge would never clear).
 *   'skipped'   — no backend (mock / unconfigured).
 */
export type MarkReadOutcome = 'marked' | 'read_only' | 'failed' | 'skipped';

/**
 * Mark messages as read in a conversation
 */
export async function markMessagesAsRead(conversationId: string, userId: string): Promise<MarkReadOutcome> {
  if (!isSupabaseConfigured() || mode.isMock) return 'skipped';

  // There used to be a second write here:
  //
  //   UPDATE messages SET read = true
  //    WHERE conversation_id = $1 AND sender_id <> $2
  //
  // against a table whose only UPDATE policy is USING (sender_id = auth.uid()).
  // The predicates exclude each other, so it matched zero rows for every caller
  // and, because RLS filters rows before a write is considered, raised nothing —
  // and it was reported `silent: true`, so it never reached Sentry either. The
  // receipt it fed was unreachable for the life of the feature.
  //
  // `messages.read` is gone (20260930120000). The receipt is now derived from the
  // per-side marks the write below maintains, so marking read is ONE write with
  // one meaning.
  //
  // This write is checked. `await` alone does NOT protect you: supabase-js
  // resolves with `{ error }` instead of throwing, so a bare `await` reports
  // success whatever the server said (ARCH-06). Unread is PER PERSON. This used to write conversations.<side>_unread_count,
  // which is one counter for a whole startup team — so a co-founder opening the
  // thread cleared everyone's badge. conversation_reads is RLS-scoped to
  // auth.uid(), so this can only ever affect the caller's own row.
  //
  // NOT silent: if this write fails the badge never clears, which is the exact
  // symptom this whole change exists to fix. It should reach Sentry.
  const { error: readsError } = await supabase
    .from('conversation_reads')
    .upsert(
      {
        conversation_id: conversationId,
        user_id: userId,
        unread_count: 0,
        last_read_at: new Date().toISOString(),
      },
      { onConflict: 'conversation_id,user_id' }
    );

  if (readsError) {
    // A sealed company's thread is read-only: an INSERT is refused with 42501,
    // and so is the ON CONFLICT UPDATE. That is the thread's designed state,
    // not a failure — reporting it would send a Sentry warning on every open.
    if (isRelationshipDenied(readsError)) return 'read_only';
    reportError(readsError, {
      context: 'markMessagesAsRead.conversationReads',
      level: 'warning',
    });
    return 'failed';
  }
  return 'marked';
}

// ============================================
// Demo Mode Helpers
// ============================================

/**
 * Simulate real-time message for demo mode
 */
export function useDemoRealtimeMessages(conversationId: string | undefined) {
  const [demoMessages, setDemoMessages] = useState<Message[]>([]);

  const addDemoMessage = useCallback(
    (message: Omit<Message, 'id' | 'created_at'>) => {
      const newMessage: Message = {
        ...message,
        id: `demo-${Date.now()}`,
        created_at: new Date().toISOString(),
      } as Message;
      setDemoMessages((prev) => [...prev, newMessage]);
    },
    []
  );

  return { demoMessages, addDemoMessage };
}
